import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { Eye, EyeOff, Pencil, Plus, Shield, Trash2, X } from "lucide-react";
import { useState } from "react";

import {
  type AdminSecret,
  deleteAdminSecret,
  fetchAdminSecret,
  fetchAdminSecrets,
  fetchAdminUsers,
  saveAdminSecret,
  updateAdminUserRole,
} from "@/lib/api";
import { useStore } from "@/lib/store";

type Tab = "secrets" | "users";

// ============================================================================
// Secret Modal
// ============================================================================

function SecretModal({
  editKey,
  onClose,
}: {
  editKey: string | null; // null = add, string = edit
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const showToast = useStore((s) => s.showToast);

  const [key, setKey] = useState(editKey ?? "");
  const [value, setValue] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [loaded, setLoaded] = useState(!editKey);

  // Pre-fill when editing
  useQuery({
    queryKey: ["admin", "secret-prefill", editKey],
    queryFn: async () => {
      if (!editKey) return null;
      const data = await fetchAdminSecret(editKey);
      setValue(data.value);
      setCategory(data.category ?? "");
      setDescription(data.description ?? "");
      setLoaded(true);
      return data;
    },
    enabled: !!editKey && !loaded,
  });

  const saveMutation = useMutation({
    mutationFn: () =>
      saveAdminSecret(key, {
        value,
        category: category || undefined,
        description: description || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "secrets"] });
      showToast(editKey ? "Secret updated" : "Secret created", "success");
      onClose();
    },
    onError: (err: Error) => {
      showToast(err.message, "error");
    },
  });

  const isValid = key.trim().length > 0 && value.length > 0 && loaded;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-lg rounded-lg border border-cyber-border bg-cyber-surface p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-cyber-text">
            {editKey ? "Edit Secret" : "Add Secret"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-cyber-muted hover:text-cyber-text"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs text-cyber-muted">Key</label>
            <input
              type="text"
              value={key}
              onChange={(e) => setKey(e.target.value.toUpperCase())}
              disabled={!!editKey}
              placeholder="GRAFANA_TOKEN"
              className="w-full rounded border border-cyber-border bg-cyber-bg px-3 py-2 text-sm text-cyber-text placeholder:text-cyber-muted/50 focus:border-cyber-cyan/50 focus:outline-none disabled:opacity-50"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-cyber-muted">Value</label>
            <textarea
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="Secret value..."
              rows={3}
              className="w-full rounded border border-cyber-border bg-cyber-bg px-3 py-2 font-mono text-sm text-cyber-text placeholder:text-cyber-muted/50 focus:border-cyber-cyan/50 focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-cyber-muted">Category</label>
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value.toLowerCase())}
              placeholder="grafana, posthog, etc."
              className="w-full rounded border border-cyber-border bg-cyber-bg px-3 py-2 text-sm text-cyber-text placeholder:text-cyber-muted/50 focus:border-cyber-cyan/50 focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-cyber-muted">Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Human-readable note"
              className="w-full rounded border border-cyber-border bg-cyber-bg px-3 py-2 text-sm text-cyber-text placeholder:text-cyber-muted/50 focus:border-cyber-cyan/50 focus:outline-none"
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-cyber-border px-4 py-2 text-sm text-cyber-muted hover:text-cyber-text"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => saveMutation.mutate()}
            disabled={!isValid || saveMutation.isPending}
            className="rounded border border-cyber-cyan/30 bg-cyber-cyan/10 px-4 py-2 text-sm text-cyber-cyan hover:bg-cyber-cyan/20 disabled:opacity-50"
          >
            {saveMutation.isPending ? "Saving..." : editKey ? "Update" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Secrets Tab
// ============================================================================

function SecretsTab() {
  const queryClient = useQueryClient();
  const showToast = useStore((s) => s.showToast);
  const [modalKey, setModalKey] = useState<string | null | false>(false); // false=closed, null=add, string=edit
  const [revealedKeys, setRevealedKeys] = useState<Set<string>>(new Set());
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const { data, isPending } = useQuery({
    queryKey: ["admin", "secrets"],
    queryFn: fetchAdminSecrets,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteAdminSecret,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "secrets"] });
      showToast("Secret deleted", "success");
      setDeleteConfirm(null);
    },
    onError: (err: Error) => {
      showToast(err.message, "error");
    },
  });

  const toggleReveal = (key: string) => {
    setRevealedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  if (isPending) {
    return (
      <div className="flex items-center justify-center py-12 text-cyber-muted">
        <span className="animate-spin-glow mr-2 inline-block">&#9680;</span> Loading secrets...
      </div>
    );
  }

  const secrets = data?.secrets ?? [];

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-cyber-muted">
          {secrets.length} secret{secrets.length !== 1 ? "s" : ""}
        </p>
        <button
          type="button"
          onClick={() => setModalKey(null)}
          className="flex items-center gap-1.5 rounded border border-cyber-cyan/30 px-3 py-1.5 text-xs text-cyber-cyan hover:bg-cyber-cyan/10"
        >
          <Plus size={14} /> Add Secret
        </button>
      </div>

      {secrets.length === 0 ? (
        <div className="py-12 text-center text-sm text-cyber-muted">
          No secrets configured. Add one to get started.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-cyber-border text-xs text-cyber-muted">
                <th className="pb-2 pr-4">Key</th>
                <th className="pb-2 pr-4">Category</th>
                <th className="pb-2 pr-4">Description</th>
                <th className="pb-2 pr-4">Updated</th>
                <th className="pb-2 pr-4">By</th>
                <th className="pb-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {secrets.map((secret) => (
                <SecretRow
                  key={secret.key}
                  secret={secret}
                  revealed={revealedKeys.has(secret.key)}
                  onToggleReveal={() => toggleReveal(secret.key)}
                  onEdit={() => setModalKey(secret.key)}
                  onDelete={() => setDeleteConfirm(secret.key)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-sm rounded-lg border border-cyber-border bg-cyber-surface p-6">
            <h3 className="mb-2 text-sm font-bold text-cyber-text">Delete Secret</h3>
            <p className="mb-4 text-sm text-cyber-muted">
              Delete <span className="font-mono text-cyber-text">{deleteConfirm}</span>? This cannot
              be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                className="rounded border border-cyber-border px-3 py-1.5 text-xs text-cyber-muted hover:text-cyber-text"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => deleteMutation.mutate(deleteConfirm)}
                disabled={deleteMutation.isPending}
                className="rounded border border-cyber-red/30 bg-cyber-red/10 px-3 py-1.5 text-xs text-cyber-red hover:bg-cyber-red/20"
              >
                {deleteMutation.isPending ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit modal */}
      {modalKey !== false && <SecretModal editKey={modalKey} onClose={() => setModalKey(false)} />}
    </div>
  );
}

function SecretRow({
  secret,
  revealed,
  onToggleReveal,
  onEdit,
  onDelete,
}: {
  secret: AdminSecret;
  revealed: boolean;
  onToggleReveal: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [revealedValue, setRevealedValue] = useState<string | null>(null);

  const handleReveal = async () => {
    if (revealed && revealedValue) {
      setRevealedValue(null);
      onToggleReveal();
      return;
    }
    try {
      const data = await fetchAdminSecret(secret.key);
      setRevealedValue(data.value);
      onToggleReveal();
    } catch {
      // Ignore
    }
  };

  return (
    <tr className="border-b border-cyber-border/50">
      <td className="py-2.5 pr-4 font-mono text-cyber-text">{secret.key}</td>
      <td className="py-2.5 pr-4 text-cyber-muted">{secret.category ?? "-"}</td>
      <td className="max-w-[200px] truncate py-2.5 pr-4 text-cyber-muted">
        {secret.description ?? "-"}
      </td>
      <td className="py-2.5 pr-4 text-xs text-cyber-muted">
        {new Date(secret.updatedAt).toLocaleDateString()}
      </td>
      <td className="py-2.5 pr-4 text-xs text-cyber-muted">{secret.updatedBy}</td>
      <td className="py-2.5 text-right">
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={handleReveal}
            className="text-cyber-muted hover:text-cyber-text"
            title={revealed ? "Hide value" : "Reveal value"}
          >
            {revealed ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
          <button
            type="button"
            onClick={onEdit}
            className="text-cyber-muted hover:text-cyber-cyan"
            title="Edit"
          >
            <Pencil size={14} />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="text-cyber-muted hover:text-cyber-red"
            title="Delete"
          >
            <Trash2 size={14} />
          </button>
        </div>
        {revealed && revealedValue && (
          <div className="mt-1 max-w-[300px] truncate text-left font-mono text-xs text-cyber-cyan">
            {revealedValue}
          </div>
        )}
      </td>
    </tr>
  );
}

// ============================================================================
// Users Tab
// ============================================================================

function UsersTab() {
  const queryClient = useQueryClient();
  const showToast = useStore((s) => s.showToast);
  const currentUser = useStore((s) => s.user);

  const { data, isPending } = useQuery({
    queryKey: ["admin", "users"],
    queryFn: fetchAdminUsers,
  });

  const roleMutation = useMutation({
    mutationFn: ({ id, role }: { id: string; role: "admin" | "member" }) =>
      updateAdminUserRole(id, role),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      showToast(`Role updated to ${vars.role}`, "success");
    },
    onError: (err: Error) => {
      showToast(err.message, "error");
    },
  });

  if (isPending) {
    return (
      <div className="flex items-center justify-center py-12 text-cyber-muted">
        <span className="animate-spin-glow mr-2 inline-block">&#9680;</span> Loading users...
      </div>
    );
  }

  const users = data?.users ?? [];

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-cyber-border text-xs text-cyber-muted">
            <th className="pb-2 pr-4">User</th>
            <th className="pb-2 pr-4">Email</th>
            <th className="pb-2 pr-4">Role</th>
            <th className="pb-2 pr-4">Joined</th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className="border-b border-cyber-border/50">
              <td className="py-2.5 pr-4">
                <div className="flex items-center gap-2">
                  {u.image ? (
                    <img src={u.image} alt="" className="h-6 w-6 rounded-full" />
                  ) : (
                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-cyber-border text-xs text-cyber-muted">
                      {u.name[0]}
                    </div>
                  )}
                  <span className="text-cyber-text">{u.name}</span>
                </div>
              </td>
              <td className="py-2.5 pr-4 text-cyber-muted">{u.email}</td>
              <td className="py-2.5 pr-4">
                <select
                  value={u.role}
                  onChange={(e) =>
                    roleMutation.mutate({ id: u.id, role: e.target.value as "admin" | "member" })
                  }
                  disabled={u.id === currentUser?.id || roleMutation.isPending}
                  className="rounded border border-cyber-border bg-cyber-bg px-2 py-1 text-xs text-cyber-text focus:border-cyber-cyan/50 focus:outline-none disabled:opacity-50"
                >
                  <option value="member">member</option>
                  <option value="admin">admin</option>
                </select>
              </td>
              <td className="py-2.5 pr-4 text-xs text-cyber-muted">
                {new Date(u.createdAt).toLocaleDateString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================
// Admin View
// ============================================================================

export function AdminView() {
  const [tab, setTab] = useState<Tab>("secrets");

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Header */}
      <div className="border-b border-cyber-border px-4 py-3">
        <div className="mx-auto flex max-w-4xl items-center gap-2">
          <Shield size={18} className="text-cyber-cyan" />
          <h1 className="text-sm font-bold text-cyber-text">Admin</h1>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="mx-auto max-w-4xl">
          {/* Tabs */}
          <div className="mb-6 flex gap-1 border-b border-cyber-border">
            <button
              type="button"
              onClick={() => setTab("secrets")}
              className={clsx(
                "px-4 py-2 text-sm transition-colors",
                tab === "secrets"
                  ? "border-b-2 border-cyber-cyan text-cyber-cyan"
                  : "text-cyber-muted hover:text-cyber-text",
              )}
            >
              Secrets
            </button>
            <button
              type="button"
              onClick={() => setTab("users")}
              className={clsx(
                "px-4 py-2 text-sm transition-colors",
                tab === "users"
                  ? "border-b-2 border-cyber-cyan text-cyber-cyan"
                  : "text-cyber-muted hover:text-cyber-text",
              )}
            >
              Users
            </button>
          </div>

          {tab === "secrets" ? <SecretsTab /> : <UsersTab />}
        </div>
      </div>
    </div>
  );
}
